import { apiFetch } from '@/lib/apiClient';
import type { Feedback, FeedbackMessage, FeedbackStatus } from '@coreltb/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const feedbackKeys = {
  all: ['feedback'] as const,
  list: () => [...feedbackKeys.all, 'list'] as const,
  messages: (id: string) => [...feedbackKeys.all, 'messages', id] as const,
};

export interface CreateFeedbackInput {
  pageUrl: string;
  pageTitle?: string | null;
  message: string;
}

export function useCreateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFeedbackInput) =>
      apiFetch<Feedback>('/api/feedback', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKeys.all }),
  });
}

/**
 * Lista wszystkich uwag — admin/dyrektor only. Polling jest aktywowany
 * przez global QueryClient config (10s).
 */
export function useFeedbackList() {
  return useQuery({
    queryKey: feedbackKeys.list(),
    queryFn: () => apiFetch<Feedback[]>('/api/feedback'),
    staleTime: 30 * 1000,
  });
}

export function useUpdateFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      apiFetch<{ id: string; status: FeedbackStatus }>(`/api/feedback/${id}`, {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKeys.all }),
  });
}

/**
 * Odpowiedz admina (Dawid) na uwage testera. Backend egzekwuje gate:
 * tylko user z email=dawidfc@gmail.com moze. Inne admini dostaja 403.
 * Pusty string = wycofanie odpowiedzi.
 */
export function useRespondFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, response }: { id: string; response: string }) =>
      apiFetch<{ id: string; adminResponse: string | null }>(`/api/feedback/${id}/respond`, {
        method: 'POST',
        body: { response },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKeys.all }),
  });
}

/**
 * Watek wiadomosci dla uwagi (po pierwszej odpowiedzi Dawida).
 * Auto-refresh 30s. Backend ack'uje seen przy GET (sygnalizator znika dla autora).
 */
export function useFeedbackMessages(feedbackId: string | null) {
  return useQuery({
    queryKey: feedbackId ? feedbackKeys.messages(feedbackId) : ['feedback', 'messages', 'none'],
    queryFn: () => apiFetch<FeedbackMessage[]>(`/api/feedback/${feedbackId}/messages`),
    enabled: Boolean(feedbackId),
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/** Nowa wiadomosc w watku — Dawid (zawsze) lub autor uwagi (po pierwszej odpowiedzi). */
export function useCreateFeedbackMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ feedbackId, content }: { feedbackId: string; content: string }) =>
      apiFetch<{ id: string; createdAt: number }>(`/api/feedback/${feedbackId}/messages`, {
        method: 'POST',
        body: { content },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: feedbackKeys.messages(vars.feedbackId) });
      qc.invalidateQueries({ queryKey: feedbackKeys.list() });
    },
  });
}
